import { useEffect, useState } from "react";

import type { CodeConfig } from "../../blockTypes";
import { useCodeRunner } from "../../hooks/useCodeRunner";
import { BLOCK_MESSAGES } from "../../messages";

interface ProgramCodeBlockProps {
    config?: CodeConfig;
}

const ProgramCodeBlock = ({ config }: ProgramCodeBlockProps) => {
    const {
        code: initialCode = "",
        language = "text",
        editable = false,
        showCompiler = false,
        showCopyButton = false
    } = config ?? {};

    // Current code (differs from the initial code when the learner edits it)
    const [code, setCode] = useState(initialCode);
    const { run, running, result, error } = useCodeRunner();

    // Keep the editor in sync if a new config is passed in
    useEffect(() => {
        setCode(initialCode);
    }, [initialCode]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
        } catch (err) {
            console.error("Failed to copy code:", err);
        }
    };

    // Shows the job output depending on its final state
    const renderResult = () => {
        if (error) {
            return <pre className="text-sm text-red-400 whitespace-pre-wrap">{error}</pre>;
        }

        if (!result) return null;

        if (result.status === "failed") {
            return (
                <pre className="text-sm text-red-400 whitespace-pre-wrap">
                    {result.message || BLOCK_MESSAGES.runFailed}
                </pre>
            );
        }

        // Completed: show stdout / stderr if present, otherwise the raw result
        const { status, stdout, stderr, ...rest } = result;

        return (
            <div className="space-y-2">
                {stdout && <pre className="text-sm text-green-400 whitespace-pre-wrap">{stdout}</pre>}
                {stderr && <pre className="text-sm text-red-400 whitespace-pre-wrap">{stderr}</pre>}
                {!stdout && !stderr && (
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(rest, null, 2)}
                    </pre>
                )}
            </div>
        );
    };

    return (
        <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-950">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-2">
                <span className="text-xs text-gray-400 font-mono">{language}</span>

                <div className="flex items-center gap-3">
                    {editable && <span className="text-xs text-gray-400">{BLOCK_MESSAGES.editable}</span>}

                    {showCopyButton && (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="text-xs text-gray-400 transition hover:text-white"
                        >
                            {BLOCK_MESSAGES.copy}
                        </button>
                    )}
                </div>
            </div>

            {/* Code: textarea when editable, read-only block otherwise */}
            {editable ? (
                <textarea
                    dir="ltr"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    spellCheck={false}
                    rows={Math.max(code.split("\n").length, 6)}
                    className="w-full resize-y bg-transparent p-4 text-left font-mono text-sm leading-6 text-gray-100 outline-none"
                />
            ) : (
                <pre
                    dir="ltr"
                    className="overflow-x-auto p-4 text-left font-mono text-sm leading-6 text-gray-100"
                >
                    <code>{code}</code>
                </pre>
            )}

            {/* Compiler */}
            {showCompiler && (
                <div className="space-y-3 border-t border-gray-800 px-4 py-3">
                    <button
                        type="button"
                        onClick={() => run(language, code)}
                        disabled={running}
                        className="rounded-md bg-green-600 px-3 py-1 text-xs text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {running ? BLOCK_MESSAGES.running : BLOCK_MESSAGES.run}
                    </button>

                    {/* Output is always LTR since it comes from the program */}
                    <div dir="ltr" className="text-left font-mono">
                        {renderResult()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramCodeBlock;